import { InfoOutlined } from '@mui/icons-material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import {
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import { type FC, type MouseEvent, useState } from 'react'
import { useDeleteLabelMaster } from 'src/features/labelMaster/api/useDeleteLabelMaster'
import { useDuplicateLabelMaster } from 'src/features/labelMaster/api/useDuplicateLabelMaster'

type Props = {
  id: string
}

export const RowActionPopover: FC<Props> = ({ id }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const deleteMutation = useDeleteLabelMaster()
  const duplicateMutation = useDuplicateLabelMaster()

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleDuplicate = () => {
    handleMenuClose()
    duplicateMutation.mutate(id)
  }

  const handleDeleteClick = () => {
    handleMenuClose()
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false)
    deleteMutation.mutate(id)
  }

  return (
    <>
      <Backdrop
        open={duplicateMutation.isPending}
        sx={{ zIndex: theme => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress />
      </Backdrop>

      <IconButton onClick={handleMenuOpen}>
        <MoreHorizIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClick={e => e.stopPropagation()}
      >
        <MenuItem onClick={handleDuplicate}>複製</MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          削除
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onClick={e => e.stopPropagation()}
        fullWidth
        maxWidth="xs"
      >
        <DialogContent sx={{ p: 4 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <InfoOutlined sx={{ color: 'warning.main' }} />
            <Typography fontWeight="bold" variant="h6" color="text.primary">
              このラベルマスタを削除しますか？
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            size="medium"
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              color: 'text.primary',
              bgcolor: grey[300],
              '&:hover': { bgcolor: grey[400] },
            }}
          >
            キャンセル
          </Button>
          <Button
            variant="text"
            color="error"
            size="medium"
            onClick={handleDeleteConfirm}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
